import exifr from 'exifr';
import { NO_CONNECTION } from '../api.js';

export const MAX_BYTES = 20 * 1024 * 1024;
export const TOO_LARGE = 'That photo is over 20 MB. Pick a smaller one.';
export const UNREADABLE = 'That photo could not be read. Pick another one.';

const isHeic = file => /hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);

async function readGps(file) {
  try {
    const g = await exifr.gps(file);
    return Number.isFinite(g?.latitude) && Number.isFinite(g?.longitude) ? { lat: g.latitude, lng: g.longitude } : null;
  } catch {
    return null;
  }
}

// createImageBitmap applies the EXIF orientation. Browsers that can't decode HEIC
// (all but Safari) get it converted to JPEG first; that library loads only then, and a
// failed load is the connection's fault, not the photo's.
async function decode(file) {
  try {
    return await createImageBitmap(file);
  } catch {}
  if (isHeic(file)) {
    let heicTo;
    try {
      ({ heicTo } = await import('heic-to'));
    } catch {
      throw new Error(NO_CONNECTION);
    }
    try {
      return await createImageBitmap(await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 }));
    } catch {}
  }
  throw new Error(UNREADABLE);
}

// A canvas re-encode carries no metadata at all. Its memory is let go at once: iOS caps
// the total canvas memory of a page.
function toJpeg(bitmap, maxEdge) {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob(b => {
      canvas.width = canvas.height = 0;
      if (b) resolve(b);
      else reject(new Error(UNREADABLE));
    }, 'image/jpeg', 0.85));
}

export async function preparePhoto(file) {
  if (file.size > MAX_BYTES) throw new Error(TOO_LARGE);
  const gps = await readGps(file);
  const bitmap = await decode(file);
  try {
    return { full: await toJpeg(bitmap, 1600), thumb: await toJpeg(bitmap, 800), gps };
  } finally {
    bitmap.close();
  }
}

// One at a time, to keep a phone's memory low.
export async function preparePhotos(files) {
  const good = [];
  const errors = [];
  for (const file of files) {
    try {
      good.push(await preparePhoto(file));
    } catch (e) {
      const message = [TOO_LARGE, NO_CONNECTION].includes(e.message) ? e.message : UNREADABLE;
      if (!errors.includes(message)) errors.push(message);
    }
  }
  return { good, errors };
}

export function photoForm(photos, data = null) {
  const form = new FormData();
  if (data) form.append('data', JSON.stringify(data));
  for (const p of photos) {
    form.append('full', p.full, 'full.jpg');
    form.append('thumb', p.thumb, 'thumb.jpg');
  }
  return form;
}
